import {
  ErpInboundItem,
  ErpInboundSaveIfStat,
  ErpInboundSaveTItem,
  SaveErpInboundsReq,
} from '@/services/inbound/erpInbound/types'

/** ISO(`yyyy-MM-dd`) → `yyyyMMdd`. BE 저장 요청 `EINDT_1` 은 `@JsonFormat(yyyyMMdd)` LocalDate 다. */
export const toYyyyMmDd = (isoDate: unknown): string => (typeof isoDate === 'string' ? isoDate.replaceAll('-', '') : '')

const asString = (value: unknown): string => (value == null ? '' : String(value))
const asOptionalString = (value: unknown): string | undefined => (value == null || value === '' ? undefined : String(value))
const asNumber = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined)

/**
 * 조회 응답(`GetErpInboundsResponse`) 1행 → 저장 요청(`RequestTItem`) 1행.
 *
 * 여기 없는 컬럼(id·AN_ID·API_FLAG·API_NO·CLS_DATE·EDITTED·INS_BY·INS_DATE·MENU_CD·ORD_REMARK·
 * SPR_NM·UPD_BY·UPD_DATE 와 PICODE·PIDATE·PIMSG·PIMSGID·PISTAT·PITIME·PIUSER)은 명시적으로
 * 뺀 것이다 — 1.0 `InboundUtil.getErpSaveRequestData` 의 구조분해 제외 목록과 같다.
 * PI* 는 행별 값이 아니라 요청 전체의 IFSTAT 하나로 간다(`toErpInboundSaveIfStat`).
 */
export const toErpInboundSaveTItem = (row: ErpInboundItem): ErpInboundSaveTItem => ({
  EBELN: asString(row.ebeln),
  EBELP: asString(row.ebelp),
  ZZ_POSN: asString(row.zzPosn),
  ETENS: asString(row.etens),
  RETPO: asString(row.retpo),
  DELVSTS: 'P',
  BEDAT: asString(row.bedAt),
  EINDT: asString(row.einDt),
  EINDT_1: toYyyyMmDd(row.einDt1),
  LIFNR: asString(row.liFnr),
  LIFTX: asString(row.liFtx),
  MATNR: asString(row.matnr),
  MAKTX: asOptionalString(row.maktx),
  CHARG: asOptionalString(row.charg),
  ZZ_SMATNR: asOptionalString(row.zzSmatnr),
  ZZ_ZBISMT: asString(row.zzZbismt),
  ZZ_SEXTWG: asOptionalString(row.zzSextwg),
  ZZ_SEWBEZ: asOptionalString(row.zzSewbez),
  ZZ_OPTION: asString(row.zzOption),
  ZZ_BARCODE: asString(row.zzBarcode),
  skuId: asOptionalString(row.skuId),
  ZZ_VKDAB: asOptionalString(row.zzVkdab),
  ZZ_VKBIS: asOptionalString(row.zzVkbis),
  ZPRODH: asOptionalString(row.zprodh),
  ZPRODH_TXT: asOptionalString(row.zprodhTxt),
  MENGE: asNumber(row.menge),
  EINQTY: asNumber(row.einQty),
  MEINS: asOptionalString(row.meins),
  NETPR: asNumber(row.netPr),
  NETPR_1: asNumber(row.netPr1),
  PEINH: asOptionalString(row.peinh),
  WAERS: asOptionalString(row.waers),
  NETWR: asNumber(row.netWr),
  NETWR_1: asNumber(row.netWr1),
  CMENGE: asNumber(row.cmenge),
  ELIKZ: asOptionalString(row.elikz),
  QCSTS: asOptionalString(row.qcsts),
  WERKS: asString(row.werks),
  NAME1: asOptionalString(row.name1),
  LGORT: asOptionalString(row.lgort),
  LGOBE: asOptionalString(row.lgobe),
  AFNAM: asOptionalString(row.afnam),
  ERNAM: asOptionalString(row.ernam),
  EMLIF: asOptionalString(row.emlif),
  LIFTX_1: asOptionalString(row.liFtx1),
  DELVNO: asString(row.delvNo),
  DELVSEQ: asOptionalString(row.delvSeq),
  IMPNR: asOptionalString(row.impnr),
  FRBNR: asOptionalString(row.frbnr),
  DLVLND: asOptionalString(row.dlvLnd),
  PSTLZ: asOptionalString(row.pstlz),
  DLVNAME: asOptionalString(row.dlvName),
  DLVTEL: asOptionalString(row.dlvTel),
  MAKERNM: asOptionalString(row.makerNm),
  VFDAT: asOptionalString(row.vfdat),
  LICHA: asOptionalString(row.licha),
  MAKER: asOptionalString(row.maker),
  HSDAT: asOptionalString(row.hsdAt),
  BTEXT_1: asOptionalString(row.btext1),
  BTEXT_2: asOptionalString(row.btext2),
  BTEXT_3: asOptionalString(row.btext3),
  ORD_TYPE: asOptionalString(row.orderType),
  WH_AREA: asOptionalString(row.warehouseArea),
  TRS_TYPE: asOptionalString(row.transportType),
  WH_NM: asOptionalString(row.warehouseName),
  PARTNER: asOptionalString(row.partner),
  NAME_GP: asOptionalString(row.nameGp),
  GENDER: asOptionalString(row.gender),
  ZZ_RFID: asOptionalString(row.zzRfid),
})

/**
 * IFSTAT — 저장 요청 전체에 하나만 실리고 BE 가 이 값을 요청에 실린 **모든 행**의 PI 컬럼 7개에
 * 동일하게 덮어쓴다(`RequestErpInboundService.update`). 행별로 다른 값을 못 받는 구조다.
 *
 * 1.0 `InboundUtil.getErpSaveRequestData` 는 `headData[0]`(요청에 실리는 배열의 첫 행) 값을 썼다.
 * 1.0 EGR01 저장도 사실은 전건이 아니라 체크박스로 고른 행만 보낸다(`EGR01.js` `gridData` 가 항상
 * 비어 있어 `CommonUtil.onSaveData` 는 `getSelectedNodes()` 결과만 돈다) — 즉 1.0 에도 이미
 * "선택한 행이 서로 다른 PI 배치에 걸치면 뒤 배치의 PI 이력이 앞 배치 값으로 덮인다" 위험이 있다.
 *
 * dev 실측(WERKS=1000, API_FLAG=FALSE, 2026-08-28): 대기 2575행에 DELVNO(배치) 318개,
 * PIDATE 100개, PITIME 295개 — 이론이 아니라 실제로 자주 섞인다. 그런데도 검증으로 막지 않고
 * 1.0 과 같은 동작을 유지하기로 결정했다(fe-60, LOGISPRD-11243) — 이 화면만 막으면 오늘 되던
 * 저장이 안 되게 되어 파리티가 더 나빠진다. **"요청 배열의 [0]"** 을 정렬·선택 순서와 무관하게
 * 명시적으로 고정한다.
 */
export const toErpInboundSaveIfStat = (rows: ErpInboundItem[]): ErpInboundSaveIfStat => {
  const first = rows[0]
  return {
    PICODE: asString(first?.piCode),
    PISTAT: asString(first?.piStat),
    PIDATE: asString(first?.piDate),
    PITIME: asString(first?.piTime),
    PIUSER: asString(first?.piUser),
    PIMSG: asString(first?.piMsg),
    PIMSGID: asString(first?.piMsgId),
  }
}

/** 저장 요청 본문 조립. `rows` 는 저장 대상(선택 행)이며 화면이 순서를 결정한다. */
export const buildErpInboundSaveBody = (rows: ErpInboundItem[]): SaveErpInboundsReq => ({
  T_ITEM: rows.map(toErpInboundSaveTItem),
  IFSTAT: toErpInboundSaveIfStat(rows),
})
